// Customer account activation — enter phone/email + code the admin gave you.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { extractError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { KeyRound, ShieldCheck, ArrowLeft } from "lucide-react";
import BackButton from "@/components/BackButton";

export default function Activate() {
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { setSessionFromReset } = useAuth();

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/activate", {
        identifier: identifier.trim(),
        code: code.trim(),
      });
      if (setSessionFromReset) setSessionFromReset(data);
      nav("/");
    } catch (e2) { setErr(extractError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 gradient-warm relative">
      <div className="absolute top-4 right-4"><BackButton to="/login"/></div>
      <div className="w-full max-w-md bg-white rounded-3xl talabya-shadow-soft p-8 animate-fade-up" data-testid="activate-page">
        <Link to="/welcome" className="flex items-center gap-2 mb-4">
          <img src="/icon-192.png" alt="طلبية" className="w-12 h-12 rounded-xl object-cover" style={{boxShadow:"0 3px 8px rgba(37,71,208,0.3)"}}/>
          <div className="font-black text-xl" style={{fontFamily:'Cairo'}}>طلبية</div>
        </Link>
        <div className="flex items-center gap-2 text-primary-brand mb-1">
          <KeyRound size={20}/>
          <h1 className="text-2xl font-black" style={{fontFamily:'Cairo'}}>تفعيل الحساب</h1>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mb-6">
          أدخل بريدك أو رقم هاتفك والكود الذي أعطاك إياه مدير طلبية لتفعيل حسابك.
        </p>

        <form onSubmit={submit} className="flex flex-col gap-3">
          <div>
            <label className="text-sm font-bold">البريد أو رقم الهاتف</label>
            <input type="text" required value={identifier} onChange={(e) => setIdentifier(e.target.value)}
              dir="ltr"
              className="mt-1 w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
              placeholder="example@mail.com  أو  09XXXXXXXX"
              data-testid="activate-identifier"
            />
          </div>
          <div>
            <label className="text-sm font-bold">كود التفعيل (6 أرقام)</label>
            <input type="text" inputMode="numeric" maxLength={6} required value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-center tracking-[8px] font-black text-2xl"
              placeholder="——————"
              autoComplete="one-time-code"
              data-testid="activate-code"
            />
          </div>
          {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-testid="activate-error">{err}</div>}
          <button disabled={busy || code.length !== 6}
            className="w-full py-3 rounded-xl bg-primary-brand text-white font-black text-base hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2 talabya-shadow-float"
            data-testid="activate-submit">
            <ShieldCheck size={18}/> {busy ? "جاري التفعيل..." : "تفعيل الحساب"}
          </button>
        </form>

        <div className="mt-6 text-xs text-[var(--text-secondary)] flex items-start gap-2 bg-blue-50 p-3 rounded-lg">
          <div>
            <strong>لا يوجد كود بعد؟</strong> تواصل مع إدارة طلبية عبر الواتساب أو الاتصال ليتم الموافقة على حسابك وإصدار كودك.
          </div>
        </div>

        <div className="mt-4 text-center text-sm">
          <Link to="/login" className="font-bold text-primary-brand inline-flex items-center gap-1">
            <ArrowLeft size={12}/> العودة لتسجيل الدخول
          </Link>
        </div>
      </div>
    </div>
  );
}
